'use client'

import React from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '@kiao-voice/ui-kit'

/**
 * The end-of-article call to action.
 *
 * Owned by the template and fixed at the foot of every article — never part of
 * the editor document. That is what keeps「one primary action per page」true:
 * an editor can pick a variant, not lay another CTA out mid-article.
 *
 * Solid red is affordable here even though the site's standing entry points are
 * ink-black: this page has no brush chip, so this is its only filled focus.
 *
 * A client component because `ButtonLink` reaches the ui-kit barrel, which also
 * exports hook-using components and therefore cannot be pulled into a server
 * component's module graph.
 */
export function ArticleCta() {
  const { t } = useTranslation('web')
  return (
    <section className="mt-10 border-t-[1.5px] border-b border-t-[var(--fg)] border-b-[var(--border)] px-0 pt-[30px] pb-8 text-center max-[620px]:mt-[30px] max-[620px]:pt-6 max-[620px]:pb-[26px]">
      <h2 className="m-0 font-[var(--font-display)] text-[length:var(--fs-section-h2)] leading-[1.35]">
        {t('blog.ctaTitle')}
      </h2>
      <p className="mx-auto mt-3 text-[length:var(--text-md)] leading-[1.8] text-[var(--muted)] max-[620px]:mt-2.5">
        {t('blog.ctaBody')}
      </p>
      <div className="mt-[22px] flex justify-center max-[620px]:mt-[18px]">
        <ButtonLink as={Link} variant="brand" href="/batch-transcription">
          {t('blog.ctaButton')}
        </ButtonLink>
      </div>
      <p className="mt-3.5 text-[length:var(--text-base)]">
        <Link
          href="/pricing"
          className="border-b border-transparent pb-0.5 text-[var(--brand)] after:content-['_→'] hover:border-[var(--brand)] focus-visible:rounded-[3px] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--fg)]"
        >
          {t('blog.ctaSecondary')}
        </Link>
      </p>
    </section>
  )
}
